import { toggleHud } from '../shared/hud-toggle.js';

/**
 * Lightweight DOM HUD for the classic 2D games. Floats above the felt.
 * Built from scratch (no shared HTML container) so each game page can just
 * mount a felt + the renderer takes care of the rest.
 */
export interface ClassicHudOptions {
  root: HTMLElement;
  title: string;
  statusText(): string | null;
  onUndo(): void;
  onNewGame(): void;
  onDaily(): void;
}

export interface ClassicHud {
  recordMove(): void;
  resetStats(): void;
  refreshStatus(): void;
  setUndoEnabled(enabled: boolean): void;
  showWin(): void;
  showLose(): void;
  hideOverlay(): void;
}

export function createClassicHud(opts: ClassicHudOptions): ClassicHud {
  const bar = document.createElement('div');
  bar.className = 'cl2-hud';
  bar.innerHTML = `
    <a class="cl2-hud__back" href="/classic/">← Classic</a>
    <h1 class="cl2-hud__title">${escapeHtml(opts.title)}</h1>
    <div class="cl2-hud__stats">
      <span class="cl2-hud__moves" data-role="moves">0 moves</span>
      <span class="cl2-hud__time" data-role="time">0:00</span>
      <span class="cl2-hud__status" data-role="status"></span>
    </div>
    <button type="button" class="cl2-hud__btn" data-role="undo" title="Undo (⌘Z)">Undo</button>
    <button type="button" class="cl2-hud__btn" data-role="daily" title="Today's deal">Daily</button>
    <button type="button" class="cl2-hud__btn" data-role="new">New</button>
    <button type="button" class="cl2-hud__btn" data-role="rules" title="How to play" hidden>Rules</button>
    <button type="button" class="cl2-hud__btn cl2-hud__gear" data-role="hud-toggle" title="Hide / show HUD" aria-label="Hide / show HUD">⚙</button>
  `;
  opts.root.appendChild(bar);
  bar.querySelector('[data-role=hud-toggle]')!.addEventListener('click', () => toggleHud());
  bar.querySelector('[data-role=daily]')!.addEventListener('click', () => opts.onDaily());

  // How-to-play modal - content comes from a <template id="rules-content">
  // that cl2-game.html fills from the matching modern game's markdown.
  const rulesTpl = document.getElementById('rules-content') as HTMLTemplateElement | null;
  const rulesBtn = bar.querySelector('[data-role=rules]') as HTMLButtonElement;
  if (rulesTpl && rulesTpl.innerHTML.trim()) {
    rulesBtn.hidden = false;
    const modal = document.createElement('div');
    modal.className = 'cl2-rules';
    modal.hidden = true;
    modal.innerHTML = `
      <div class="cl2-rules__card">
        <button type="button" class="cl2-rules__close" data-role="rules-close" aria-label="Close">×</button>
        <div class="cl2-rules__title">${escapeHtml(rulesTpl.dataset.title ?? opts.title)}</div>
        <div class="cl2-rules__body">${rulesTpl.innerHTML}</div>
      </div>`;
    opts.root.appendChild(modal);
    const closeRules = () => { modal.hidden = true; };
    rulesBtn.addEventListener('click', () => { modal.hidden = false; });
    modal.querySelector('[data-role=rules-close]')!.addEventListener('click', closeRules);
    modal.addEventListener('click', (e) => { if (e.target === modal) closeRules(); });
    window.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeRules(); });
  }

  const movesEl = bar.querySelector('[data-role=moves]')! as HTMLElement;
  const timeEl = bar.querySelector('[data-role=time]')! as HTMLElement;
  const statusEl = bar.querySelector('[data-role=status]')! as HTMLElement;
  const undoBtn = bar.querySelector('[data-role=undo]')! as HTMLButtonElement;
  const newBtn = bar.querySelector('[data-role=new]')! as HTMLButtonElement;

  const overlay = document.createElement('div');
  overlay.className = 'cl2-overlay';
  overlay.hidden = true;
  overlay.innerHTML = `
    <div class="cl2-overlay__card">
      <div class="cl2-overlay__title" data-role="overlay-title">You won</div>
      <div class="cl2-overlay__stats">
        <span data-role="overlay-moves">0</span> moves &middot;
        <span data-role="overlay-time">0:00</span>
      </div>
      <button type="button" class="cl2-overlay__btn" data-role="overlay-replay">Play again</button>
    </div>
  `;
  opts.root.appendChild(overlay);
  const overlayTitleEl = overlay.querySelector('[data-role=overlay-title]')! as HTMLElement;
  const overlayMovesEl = overlay.querySelector('[data-role=overlay-moves]')! as HTMLElement;
  const overlayTimeEl = overlay.querySelector('[data-role=overlay-time]')! as HTMLElement;
  const overlayReplay = overlay.querySelector('[data-role=overlay-replay]')! as HTMLButtonElement;

  let movesCount = 0;
  let startedAt = 0;
  let timerStarted = false;

  function fmtTime(sec: number): string {
    const mm = Math.floor(sec / 60);
    const ss = sec % 60;
    return `${mm}:${ss.toString().padStart(2, '0')}`;
  }

  function tick() {
    movesEl.textContent = `${movesCount} ${movesCount === 1 ? 'move' : 'moves'}`;
    const sec = timerStarted ? Math.floor((performance.now() - startedAt) / 1000) : 0;
    timeEl.textContent = fmtTime(sec);
  }
  setInterval(tick, 250);
  tick();

  undoBtn.addEventListener('click', () => opts.onUndo());
  newBtn.addEventListener('click', () => opts.onNewGame());
  overlayReplay.addEventListener('click', () => {
    hud.hideOverlay();
    opts.onNewGame();
  });
  window.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
      e.preventDefault();
      opts.onUndo();
    }
  });

  const hud: ClassicHud = {
    recordMove() {
      movesCount++;
      if (!timerStarted) {
        timerStarted = true;
        startedAt = performance.now();
      }
      tick();
    },
    resetStats() {
      movesCount = 0;
      timerStarted = false;
      startedAt = 0;
      statusEl.textContent = '';
      statusEl.classList.remove('cl2-hud__status--show');
      hud.hideOverlay();
      tick();
    },
    refreshStatus() {
      const text = opts.statusText();
      if (text) {
        statusEl.textContent = text;
        statusEl.classList.add('cl2-hud__status--show');
      } else {
        statusEl.classList.remove('cl2-hud__status--show');
      }
    },
    setUndoEnabled(enabled) {
      undoBtn.disabled = !enabled;
    },
    showWin() {
      overlayTitleEl.textContent = 'You won';
      overlayMovesEl.textContent = String(movesCount);
      overlayTimeEl.textContent = timeEl.textContent ?? '0:00';
      overlay.classList.remove('cl2-overlay--lose');
      overlay.hidden = false;
    },
    showLose() {
      overlayTitleEl.textContent = 'No moves left';
      overlayMovesEl.textContent = String(movesCount);
      overlayTimeEl.textContent = timeEl.textContent ?? '0:00';
      overlay.classList.add('cl2-overlay--lose');
      overlay.hidden = false;
    },
    hideOverlay() {
      overlay.classList.remove('cl2-overlay--lose');
      overlay.hidden = true;
    },
  };
  return hud;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;'
    : c === '<' ? '&lt;'
    : c === '>' ? '&gt;'
    : c === '"' ? '&quot;'
    : '&#39;');
}
